import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Subscription } from "rxjs";

export interface EthnicityRaceSyncConfig {
  ethnicityControlName?: string;
  hopeEthnicityControlName?: string;
  hopeRaceControlName?: string;
  ethnicityItems?: any[];
  hopeEthnicityItems?: any[];
  hopeRaceItems?: any[];
}

export interface SyncState {
  preserveHisValues: boolean;
  initialHisValues: number[];
  initialHopeEthnicityValues: number[];
  initialHopeRaceValues: number[];
}

@Injectable()
export class EthnicityRaceSyncService {
  private static readonly MUTUALLY_EXCLUSIVE_OPTIONS = [
    "Patient Declines to Respond",
    "None of the above",
  ];

  // Race (HOPE) → Ethnicity / Race (HIS) mappings
  private static readonly RACE_TO_HIS_MAPPINGS = {
    White: "White",
    Black_or_African_American: "Black or African American",
    American_Indian_or_Alaska_Native: "American Indian or Alaska Native",
    Asian_Indian: "Asian",
    Chinese: "Asian",
    Filipino: "Asian",
    Japanese: "Asian",
    Korean: "Asian",
    Vietnamese: "Asian",
    Other_Asian: "Asian",
    Native_Hawaiian: "Native Hawaiian or Other Pacific Islander",
    Guamanian_or_Chamorro: "Native Hawaiian or Other Pacific Islander",
    Samoan: "Native Hawaiian or Other Pacific Islander",
    Other_Pacific_Islander: "Native Hawaiian or Other Pacific Islander",
  };

  // Ethnicity (HOPE) → Ethnicity / Race (HIS) mappings
  private static readonly ETHNICITY_TO_HIS_MAPPINGS = {
    Hispanic_or_Latino: "Hispanic or Latino",
    Yes_Mexican_Mexican_American_or_Chicano_a: "Hispanic or Latino",
    Yes_Puerto_Rican: "Hispanic or Latino",
    Yes_Cuban: "Hispanic or Latino",
    Yes_Another_Hispanic_Latino_a_or_Spanish_Origin: "Hispanic or Latino",
  };

  setupSynchronization(
    formGroup: FormGroup,
    config: EthnicityRaceSyncConfig
  ): { subscriptions: Subscription[]; syncState: SyncState } {
    const {
      ethnicityControlName = "ethnicity",
      hopeEthnicityControlName = "hopeEthnicity",
      hopeRaceControlName = "hopeRace",
      ethnicityItems = [],
      hopeEthnicityItems = [],
      hopeRaceItems = [],
    } = config;

    const syncState = this.determineSyncState(formGroup, {
      ethnicityControlName,
      hopeEthnicityControlName,
      hopeRaceControlName,
    });

    const subscriptions: Subscription[] = [];

    // Setup mutually exclusive logic for HOPE fields
    subscriptions.push(
      this.setupMutuallyExclusiveControl(
        formGroup,
        hopeEthnicityControlName,
        hopeEthnicityItems
      )
    );

    subscriptions.push(
      this.setupMutuallyExclusiveControl(
        formGroup,
        hopeRaceControlName,
        hopeRaceItems
      )
    );

    // Setup synchronization if Rule 2 applies
    if (!syncState.preserveHisValues) {
      subscriptions.push(
        this.setupHopeToHisSync(
          formGroup,
          hopeEthnicityControlName,
          ethnicityControlName,
          hopeEthnicityItems,
          ethnicityItems,
          EthnicityRaceSyncService.ETHNICITY_TO_HIS_MAPPINGS
        )
      );

      subscriptions.push(
        this.setupHopeToHisSync(
          formGroup,
          hopeRaceControlName,
          ethnicityControlName,
          hopeRaceItems,
          ethnicityItems,
          EthnicityRaceSyncService.RACE_TO_HIS_MAPPINGS
        )
      );
    }

    return { subscriptions, syncState };
  }

  private determineSyncState(
    formGroup: FormGroup,
    controlNames: {
      ethnicityControlName: string;
      hopeEthnicityControlName: string;
      hopeRaceControlName: string;
    }
  ): SyncState {
    const hisValues =
      formGroup.get(controlNames.ethnicityControlName)?.value || [];
    const hopeEthnicityValues =
      formGroup.get(controlNames.hopeEthnicityControlName)?.value || [];
    const hopeRaceValues =
      formGroup.get(controlNames.hopeRaceControlName)?.value || [];

    // Rule 1: Preserve HIS values if HIS has values and both HOPE fields are empty
    const preserveHisValues =
      hisValues.length > 0 &&
      hopeEthnicityValues.length === 0 &&
      hopeRaceValues.length === 0;

    return {
      preserveHisValues,
      initialHisValues: [...hisValues],
      initialHopeEthnicityValues: [...hopeEthnicityValues],
      initialHopeRaceValues: [...hopeRaceValues],
    };
  }

  private setupMutuallyExclusiveControl(
    formGroup: FormGroup,
    controlName: string,
    items: any[]
  ): Subscription {
    return formGroup
      .get(controlName)
      .valueChanges.subscribe((selectedIds: number[]) => {
        if (!selectedIds?.length) return;

        const selectedItems = items.filter((item) =>
          selectedIds.includes(item.id)
        );
        const isMutuallyExclusive = (desc: string) =>
          EthnicityRaceSyncService.MUTUALLY_EXCLUSIVE_OPTIONS.includes(desc);

        const mutuallyExclusiveIds = selectedItems
          .filter((item) => isMutuallyExclusive(item.description))
          .map((item) => item.id);
        const regularIds = selectedItems
          .filter((item) => !isMutuallyExclusive(item.description))
          .map((item) => item.id);

        // Case 1: Multiple mutually exclusive options selected
        if (mutuallyExclusiveIds.length > 1) {
          const lastSelectedId = selectedIds.reverse().find((id) => {
            const item = items.find((i) => i.id === id);
            return item && isMutuallyExclusive(item.description);
          });

          if (lastSelectedId) {
            formGroup
              .get(controlName)
              .setValue([lastSelectedId], { emitEvent: false });
          }
        }
        // Case 2: Both mutually exclusive and regular options selected
        else if (mutuallyExclusiveIds.length > 0 && regularIds.length > 0) {
          const lastSelectedId = selectedIds[selectedIds.length - 1];
          const lastSelectedItem = items.find((i) => i.id === lastSelectedId);

          if (lastSelectedItem) {
            const isLastSelectedMutuallyExclusive = isMutuallyExclusive(
              lastSelectedItem.description
            );

            if (isLastSelectedMutuallyExclusive) {
              formGroup
                .get(controlName)
                .setValue([lastSelectedId], { emitEvent: false });
            } else {
              formGroup
                .get(controlName)
                .setValue(regularIds, { emitEvent: false });
            }
          }
        }
      });
  }

  private setupHopeToHisSync(
    formGroup: FormGroup,
    hopeControlName: string,
    hisControlName: string,
    hopeItems: any[],
    hisItems: any[],
    mappings: Record<string, string>
  ): Subscription {
    return formGroup
      .get(hopeControlName)
      .valueChanges.subscribe((selectedIds: number[]) => {
        const currentHisValues = formGroup.get(hisControlName).value || [];
        let newHisValues = [...currentHisValues];

        // Get selected HOPE descriptions
        const selectedHopeDescriptions = hopeItems
          .filter((item) => selectedIds?.includes(item.id))
          .map((item) => item.description);

        // Get all possible HOPE descriptions that could map to HIS
        const allMappableHopeDescriptions = Object.keys(mappings).map((key) =>
          key.replace(/_/g, " ")
        );

        // Remove HIS values that were previously mapped from HOPE but are no longer selected
        allMappableHopeDescriptions.forEach((hopeDesc) => {
          const hisDesc = this.findMappingValue(hopeDesc, mappings);
          const hisItem = hisItems.find((item) => item.description === hisDesc);

          if (hisItem && !selectedHopeDescriptions.includes(hopeDesc)) {
            // Check if any other selected HOPE values still map to this HIS value
            const stillMapped = selectedHopeDescriptions.some(
              (desc) => this.findMappingValue(desc, mappings) === hisDesc
            );
            if (!stillMapped) {
              newHisValues = newHisValues.filter((id) => id !== hisItem.id);
            }
          }
        });

        // Add HIS values for newly selected HOPE values
        selectedHopeDescriptions.forEach((hopeDesc) => {
          const hisDesc = this.findMappingValue(hopeDesc, mappings);
          if (hisDesc) {
            const hisItem = hisItems.find(
              (item) => item.description === hisDesc
            );
            if (hisItem && !newHisValues.includes(hisItem.id)) {
              newHisValues.push(hisItem.id);
            }
          }
        });

        // Update HIS field if values changed
        if (
          JSON.stringify(newHisValues.sort()) !==
          JSON.stringify(currentHisValues.sort())
        ) {
          formGroup
            .get(hisControlName)
            .setValue(newHisValues, { emitEvent: false });
        }
      });
  }

  getMatchingEthnicitiesIdsAndRacesIdsByDescription(
    source: any,
    items: any[],
    fieldType: string = "ethnicity"
  ): number[] {
    if (!source || !items) {
      return [];
    }

    // Handle case where source is already an array of IDs
    if (
      Array.isArray(source) &&
      source.every((item) => typeof item === "number")
    ) {
      return source.filter((id) => items.some((item) => item.id === id));
    }

    let sourceDescription = "";
    if (typeof source === "string") {
      sourceDescription = source;
    } else if (source.description) {
      sourceDescription = source.description;
    } else {
      return [];
    }

    // For HOPE fields, use intelligent parsing
    if (fieldType === "hopeEthnicity") {
      return this.parseHopeDescriptions(sourceDescription, items);
    }

    // For regular ethnicity, use comma-split logic
    const descriptionsToMatch = sourceDescription
      .split(",")
      .map((desc) => desc.trim());

    return items
      .filter((item) => descriptionsToMatch.includes(item.description))
      .map((item) => item.id);
  }

  private parseHopeDescriptions(
    sourceDescription: string,
    items: any[]
  ): number[] {
    if (!sourceDescription || !items) {
      return [];
    }

    const matchedIds: number[] = [];
    const availableDescriptions = items.map((item) => item.description);

    // Sort descriptions by length (longest first) to match longer descriptions first
    const sortedDescriptions = availableDescriptions.sort(
      (a, b) => b.length - a.length
    );

    for (const description of sortedDescriptions) {
      if (sourceDescription.includes(description)) {
        const item = items.find((i) => i.description === description);
        if (item && !matchedIds.includes(item.id)) {
          matchedIds.push(item.id);
          // Remove matched description to avoid partial matches
          sourceDescription = sourceDescription.replace(description, "");
        }
      }
    }

    return matchedIds;
  }

  private normalizeDescriptionToKey(description: string): string {
    return (
      description
        // Handle gender-inclusive patterns first (e.g., "Latino/a" -> "Latino_a", "Chicano/a" -> "Chicano_a")
        .replace(/\/a\b/g, "_a")
        // Replace commas and spaces with underscores
        .replace(/,\s*/g, "_")
        .replace(/\s+/g, "_")
        // Clean up any double underscores
        .replace(/_+/g, "_")
        // Remove leading/trailing underscores
        .replace(/^_|_$/g, "")
    );
  }

  private findMappingValue(
    hopeDescription: string,
    mappings: Record<string, string>
  ): string | null {
    // Convert the description to underscore format for lookup
    const normalizedKey = this.normalizeDescriptionToKey(hopeDescription);

    // Try exact match with normalized key
    if (mappings[normalizedKey]) {
      return mappings[normalizedKey];
    }

    // Fallback: try case-insensitive match
    const lowerKey = normalizedKey.toLowerCase();
    for (const [key, value] of Object.entries(mappings)) {
      if (key.toLowerCase() === lowerKey) {
        return value;
      }
    }

    return null;
  }

  calculateEthnicityOrRace(value: any): number {
    let x = 0;
    value.forEach((y) => {
      x += parseInt(y);
    });
    return x;
  }
}



// WEBPACK FOOTER //
// ./src/providers/ethnicity-race-sync/ethnicity-race-sync.service.ts